import { BetSummary, StakeQuickChips } from '@/components/opportunity/execute-dialog-helpers';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { newIdempotencyKey } from '@/lib/idempotency';
import { trpc } from '@/lib/trpc';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

interface OpportunityStakeShape {
  suggestedStakeUsd: number;
  kellyFractionUsed?: number;
  kellyPctApplied?: number;
}

interface OpportunityShape {
  id: string;
  market?: string;
  selection?: string;
  match?: { p1Name: string | null; p2Name: string | null } | null;
  currentBestBook: string | null;
  currentBestOdds: number | null;
  currentEdgePct?: number | null;
  targetOdds?: number;
  stake?: OpportunityStakeShape | null;
  manualStakeUsd?: number | null;
}

interface Props {
  opportunity: OpportunityShape | null;
  onClose: () => void;
}

// Confirmation modal for opportunities.markExecuted. Optimised for the
// common path: the operator took the best book + best odds and just wants
// to confirm the stake.
//
// UX:
//   - Read-only header summarises the bet.
//   - Stake = primary input; one-click chips for "use pick" / "use Kelly".
//   - Book / odds / nota live behind a collapsed "editar detalles" toggle.
export function ExecuteDialog({ opportunity, onClose }: Props) {
  const utils = trpc.useUtils();
  const [book, setBook] = useState('');
  const [odds, setOdds] = useState('');
  const [stake, setStake] = useState('');
  const [reason, setReason] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    setBook(opportunity?.currentBestBook ?? '');
    setOdds(opportunity?.currentBestOdds?.toFixed(3) ?? '');
    const manual = opportunity?.manualStakeUsd ?? 0;
    const kelly = opportunity?.stake?.suggestedStakeUsd ?? 0;
    const prefill = manual > 0 ? manual : kelly > 0 ? kelly : 0;
    setStake(prefill > 0 ? prefill.toFixed(2) : '');
    setReason('');
    setShowAdvanced(false);
  }, [opportunity]);

  const mutation = trpc.opportunities.markExecuted.useMutation({
    onSuccess: async () => {
      toast.success('Pick ejecutado', { description: `${book} @ ${odds}` });
      await Promise.all([
        utils.opportunities.list.invalidate(),
        utils.opportunities.get.invalidate(),
      ]);
      onClose();
    },
    onError: (err) => toast.error('No se pudo ejecutar', { description: err.message }),
  });

  const oddsNum = Number.parseFloat(odds);
  const stakeNum = stake.trim() === '' ? null : Number.parseFloat(stake);
  const canSubmit =
    !!opportunity &&
    book.trim().length > 0 &&
    Number.isFinite(oddsNum) &&
    oddsNum >= 1.01 &&
    oddsNum <= 1000 &&
    (stakeNum === null || (Number.isFinite(stakeNum) && stakeNum >= 0));

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit || !opportunity) return;
    mutation.mutate({
      opportunityId: opportunity.id,
      executedBook: book.trim(),
      executedOdds: oddsNum,
      executedStakeUsd: stakeNum ?? undefined,
      reason: reason.trim() || undefined,
      idempotencyKey: newIdempotencyKey(),
    });
  };

  const manualStake = opportunity?.manualStakeUsd ?? 0;
  const kellyStake = opportunity?.stake?.suggestedStakeUsd ?? 0;

  return (
    <Dialog
      open={opportunity !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Confirmar ejecución</DialogTitle>
          <DialogDescription>
            Confirmá el stake. Por defecto se registra al mejor book con la mejor cuota.
          </DialogDescription>
        </DialogHeader>

        {opportunity && <BetSummary opportunity={opportunity} />}

        <form onSubmit={handleSubmit} className="grid gap-3">
          <div className="grid gap-1.5">
            <div className="flex items-baseline justify-between">
              <Label htmlFor="exec-stake" className="font-mono text-xs uppercase tracking-wider">
                Stake USD
              </Label>
              <StakeQuickChips
                manual={manualStake}
                kelly={kellyStake}
                onPick={(v) => setStake(v.toFixed(2))}
              />
            </div>
            <Input
              id="exec-stake"
              type="number"
              step="1"
              min="0"
              value={stake}
              onChange={(e) => setStake(e.target.value)}
              placeholder="—"
              className="text-lg font-mono"
              autoFocus
            />
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="inline-flex items-center gap-1 self-start text-[11px] text-muted-foreground hover:text-foreground"
          >
            {showAdvanced ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
            {showAdvanced ? 'Ocultar detalles' : 'Editar book / cuota / nota'}
          </button>
          {showAdvanced && (
            <div className="grid gap-3 rounded-md border border-border bg-muted/30 p-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="exec-book" className="text-xs">
                    Book
                  </Label>
                  <Input
                    id="exec-book"
                    value={book}
                    onChange={(e) => setBook(e.target.value)}
                    placeholder="pinnacle"
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="exec-odds" className="text-xs">
                    Cuota ejecutada
                  </Label>
                  <Input
                    id="exec-odds"
                    type="number"
                    step="0.001"
                    min="1.01"
                    max="1000"
                    value={odds}
                    onChange={(e) => setOdds(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="exec-reason" className="text-xs">
                  Nota (opcional)
                </Label>
                <Input
                  id="exec-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={500}
                  placeholder="Por qué tomé un precio distinto…"
                />
              </div>
            </div>
          )}

          <DialogFooter className="mt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSubmit || mutation.isPending}>
              {mutation.isPending ? 'Ejecutando…' : 'Confirmar ejecución'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
